import React, { useState, useEffect, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useManualQuery, useMutation } from "graphql-hooks";
import { useNavigate, Link } from "react-router-dom";

import {
  TextField,
  Box,
  Divider,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  Avatar,
  Select,
  MenuItem,
} from "@mui/material";

import AlertPopup from "../components/AlertPopup.js";
import { styled } from "@mui/material/styles";

import {
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  VisibilityOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowDropDown,
} from "@mui/icons-material";

import {
  ALL_TOYS_LIST,
  DELETE_TOY_DATA_BY_UUID,
  UPDATE_TOY_DATA_BY_UUID,
} from "../graphQL/toy_qry";

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

// UserLink Component
const UserLink = ({ username, userUuid }) => (
  <StyledTooltip title="View User's Page">
    <Link
      to={`/user/${userUuid}/profile`}
      target="_blank"
      style={{
        color: "#d32f2f",
        textDecoration: "none",
        fontSize: "14px",
        display: "inline-block",
        lineHeight: 1,
      }}
      onMouseEnter={e => {
        e.target.style.textDecoration = "underline";
      }}
      onMouseLeave={e => {
        e.target.style.textDecoration = "none";
      }}
    >
      {username}
    </Link>
  </StyledTooltip>
);

const RootDiv = styled("div")({
  marginRight: "8rem",
  textAlign: "center",
  padding: 2,
  paddingLeft: 0,
  paddingRight: 0,
  width: "1400px",
  //  minWidth: "500px",
});

const ContainerDiv = styled("div")({
  padding: 32,
  marginBottom: "5rem",
  clear: "both",
  borderRadius: "8px",
  minHeight: "870px",
  backgroundColor: "#ffffff",
});

export default function AdminToysList() {
  const navigate = useNavigate();
  const [isPaginationMenuOpen, setIsPaginationMenuOpen] = useState(false);
  const paginationSelectRef = useRef(null);
  const paginationMenuRef = useRef(null);
  // Alert popup state
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    toyId: null,
    errorMsg: "",
  });

  const [statusFilter, setStatusFilter] = useState("All");
  const [rows, setRows] = useState([]);

  // Toy Mutation
  const [toys, setToys] = useState([]);
  const [getAllToys] = useManualQuery(ALL_TOYS_LIST);
  const [deleteToy] = useMutation(DELETE_TOY_DATA_BY_UUID);
  const [updateToyStatus] = useMutation(UPDATE_TOY_DATA_BY_UUID);

  // Get data from database
  useEffect(() => {
    allToysData();
  }, []);

  const allToysData = async () => {
    const {
      loading: loadingAllToysData,
      error: errorAllToysData,
      data: dataAllToysData,
    } = await getAllToys();
    if (!loadingAllToysData && dataAllToysData) {
      console.log(dataAllToysData);
      const toysData = Object.values(dataAllToysData.allToys).map(item => ({
        id: Number(item.toyId),
        uuid: item.uuid,
        toyImage: item.toyImages.at(-1)?.fileName,
        username: item.userId.username,
        useruuid: item.userId.userUuid,
        title: item.title,
        dateCreated: item.createDate,
        status: item.status,
        publishedOn: item.isPublished ? "Published" : "Draft",
        datePublished: item.isPublished ? item.publishedDate : "",
      }));
      setToys(toysData);
    }
  };

  // Initialize data
  useEffect(() => {
    setRows(getFilteredData);
  }, [statusFilter, toys]);

  const getFilteredData = () => {
    let filteredData = toys;
    if (statusFilter !== "All") {
      if (statusFilter === "Draft") {
        filteredData = filteredData.filter(toy => toy.publishedOn === "Draft");
      } else {
        filteredData = filteredData.filter(
          toy => toy.status === statusFilter && toy.publishedOn !== "Draft"
        );
      }
    }
    return filteredData;
  };

  // Status filter change handlers
  const handleStatusFilterChange = event => {
    setStatusFilter(event.target.value);
    // setPage(0); // Reset to first page when filter changes
  };

  const dateFormatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true, // change to false if you prefer 24h format
  });

  const dayFormat = day => {
    const dayDate = new Date(day);
    return formatter.format(dayDate);
  };

  const dateFormat = date => {
    const dayDate = new Date(date);
    return dateFormatter.format(dayDate);
  };

  const columns = [
    {
      field: "toyImage",
      headerName: "Image",
      minWidth: 5,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Avatar
              src={rows.row.toyImage}
              alt={rows.row.objectName}
              sx={{
                width: 80,
                height: 80,
                borderRadius: "8px",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "username",
      headerName: "Creator",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return <UserLink username={rows.row.username} userUuid={rows.row.useruuid} />;
      },
    },
    {
      field: "title",
      headerName: "Title",
      minWidth: 10,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: params => (
        <div
          style={{
            whiteSpace: "normal", // ⬅️ allows wrapping
            wordBreak: "break-word",
            lineHeight: "1.2",
            textAlign: "left",
            padding: "0 4px",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "dateCreated",
      headerName: "Date Created",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <StyledTooltip title={dayFormat(rows.row.dateCreated)} arrow>
            <span
              style={{
                display: "inline-block",
                lineHeight: 1.5,
                verticalAlign: "middle",
              }}
            >
              {dateFormat(rows.row.dateCreated)}
            </span>
          </StyledTooltip>
        );
      },
    },
    {
      field: "datePublished",
      headerName: "Date Published",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        if (rows.row.datePublished === "") return "";
        return (
          <StyledTooltip title={dayFormat(rows.row.datePublished)} arrow>
            <span
              style={{
                display: "inline-block",
                lineHeight: 1.5,
                verticalAlign: "middle",
              }}
            >
              {dateFormat(rows.row.datePublished)}
            </span>
          </StyledTooltip>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 20,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              color: "#333",
              border: "1px solid #000",
              fontSize: "12px",
              fontWeight: "500",
              width: "80px",
              height: "30px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            {rows.row.status}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 1,
      sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {rows.row.publishedOn === "Draft" ? (
              <StyledTooltip title="Publish this toy">
                <IconButton
                  size="small"
                  // onClick={() => handlePublishClick(rows.row.id)}
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <AssignmentTurnedInOutlined fontSize="small" />
                </IconButton>
              </StyledTooltip>
            ) : (
              <StyledTooltip title="Unpublish this toy">
                <IconButton
                  size="small"
                  // onClick={() => handleUnpublishClick(rows.row.id)}
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <FactCheckOutlined fontSize="small" />
                </IconButton>
              </StyledTooltip>
            )}
            <StyledTooltip title="View this toy">
              <IconButton
                component={Link}
                to={`/toy/${rows.row.uuid}`}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Edit this toy">
              <IconButton
                component={Link}
                to={`/toy/${rows.row.uuid}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Delete this toy">
              <IconButton
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
                onClick={() => handleDeleteClick(rows.row.id)}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
          </Box>
        );
      },
    },
  ];

  function DataGridTitle() {
    return (
      <>
        <Grid
          container
          xs={12}
          sx={{
            pt: 2,
            pb: 2,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: 20,
              fontWeight: 500,
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              margin: "0 auto",
            }}
          >
            Toys List
          </Typography>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            sx={{
              width: "170px",
              paddingRight: 2,
              "& .MuiSelect-select": {
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              },
              "& .MuiOutlinedInput-root": {
                height: "40px",
                backgroundColor: "white",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#666",
                fontSize: "14px",
                "&.Mui-focused": {
                  color: "#666",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
            }}
            SelectProps={{
              IconComponent: ArrowDropDown,
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      justifyContent: "flex-start",
                      textAlign: "left",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="For Sale">For Sale</MenuItem>
            <MenuItem value="Sold">Sold</MenuItem>
            <MenuItem value="Shipped">Shipped</MenuItem>
            <MenuItem value="Received">Received</MenuItem>
            <MenuItem value="Accepted">Accepted</MenuItem>
            {/* --- Divider --- */}
            <MenuItem disabled sx={{ padding: 0 }}>
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: "rgba(0,0,0,0.12)",
                  margin: "4px 0",
                }}
              />
            </MenuItem>
            <MenuItem value="Draft">Draft</MenuItem>
          </TextField>
        </Grid>
        <Divider />
      </>
    );
  }

  useEffect(() => {
    const handleScroll = event => {
      if (isPaginationMenuOpen) {
        const isScrollingInsideDropdown =
          (paginationMenuRef.current &&
            paginationMenuRef.current.contains(event.target)) ||
          (paginationSelectRef.current &&
            paginationSelectRef.current.contains(event.target));

        if (!isScrollingInsideDropdown) {
          setIsPaginationMenuOpen(false);
        }
      }
    };

    if (isPaginationMenuOpen) {
      document.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isPaginationMenuOpen]);

  const handleConfirmAction = async () => {
    const toyUuid = toys.find(item => item.id === alertPopup.toyId)?.uuid;

    if (alertPopup.type === "publish") {
      // Change it into database
      // const {
      //   loading: loadingToyStatus,
      //   error: errorToyStatus,
      //   data: toyStatus,
      // } = await updateToyStatus({
      //   variables: {
      //     toyUuid,
      //     isPublished: true,
      //   },
      // });
      // if (!loadingToyStatus && ToyStatus && toyStatus.updateToyStatus) {
      //   // Update Toy to published
      //   setToys(prevToys =>
      //     prevToys.map(toy =>
      //       toy.id === alertPopup.toyId
      //         ? {
      //             ...toy,
      //             datePublished: new Date(),
      //             status: "Published",
      //           }
      //         : toy
      //     )
      //   );
      // } else {
      //   console.log("errorToyData: ", errorToyStatus);
      // }
    } else if (alertPopup.type === "unpublish") {
      // // Change it into database
      // const {
      //   loading: loadingToyStatus,
      //   error: errorToyStatus,
      //   data: toyStatus,
      // } = await updateToyStatus({
      //   variables: {
      //     toyUuid,
      //     isPublished: false,
      //   },
      // });
      // if (!loadingToyStatus && toyStatus && toyStatus.updateToyStatus) {
      //   // Update Toy to draft
      //   setToys(prevToys =>
      //     prevToys.map(toy =>
      //       toy.id === alertPopup.toyId
      //         ? { ...toy, datePublished: "", status: "Draft" }
      //         : toy
      //     )
      //   );
      // } else {
      //   console.log("errorToyData: ", errorToyStatus);
      // }
    } else if (alertPopup.type === "delete") {
      // Backend for removing it from database
      const {
        loading: loadingToyDelete,
        error: errorToyDelete,
        data: toyDelete,
      } = await deleteToy({
        variables: {
          toyUuid,
        },
      });
      if (!loadingToyDelete && toyDelete && toyDelete.deleteToyByUuid.success) {
        // Remove Toy from list
        setToys(prevToys => prevToys.filter(toy => toy.id !== alertPopup.toyId));
        setAlertPopup({ open: false, type: "", toyId: null, errorMsg: "" });
      } else {
        if (toyDelete.deleteToyByUuid.success === false) {
          setAlertPopup({
            open: true,
            type: "error",
            toyId: null,
            errorMsg: toyDelete.deleteToyByUuid.msg,
          });
        } else {
          console.log("errorToyDelete: ", errorToyDelete);
        }
      }
    }
  };

  const handleCancelAction = () => {
    setAlertPopup({ open: false, type: "", toyId: null, errorMsg: "" });
  };

  // Popup handlers
  // const handlePublishClick = toyId => {
  //   setAlertPopup({
  //     open: true,
  //     type: "publish",
  //     toyId: toyId,
  //     errorMsg: ""
  //   });
  // };

  // const handleUnpublishClick = toyId => {
  //   setAlertPopup({
  //     open: true,
  //     type: "unpublish",
  //     toyId: toyId,
  //     errorMsg: ""
  //   });
  // };

  const handleDeleteClick = toyId => {
    setAlertPopup({
      open: true,
      type: "delete",
      toyId: toyId,
      errorMsg: "",
    });
  };

  return (
    <RootDiv>
      <title>R3PLAY - Report Management – Admin</title>
      <ContainerDiv>
        <DataGrid
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
              position: "relative",
              zIndex: 1,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
            // Firefox-specific rendering fix
            "@-moz-document url-prefix()": {
              "& .MuiDataGrid-columnHeaders": {
                transform: "translateY(0.1px)",
              },
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: 12,
            },
          }}
          columns={columns}
          rows={rows}
          showCellRightBorder={true}
          slots={{ toolbar: DataGridTitle }}
          getRowId={row => row.id}
          autoHeight={true}
          rowHeight={90}
          experimentalFeatures={{ newEditingApi: true }}
          pagination
          pageSizeOptions={[10, 20, 50]} // user can select page size
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } }, // default to 10 rows
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          slotProps={{
            pagination: {
              SelectProps: {
                open: isPaginationMenuOpen,
                onOpen: () => setIsPaginationMenuOpen(true),
                onClose: () => setIsPaginationMenuOpen(false),
                ref: paginationSelectRef,
                MenuProps: {
                  PaperProps: {
                    ref: paginationMenuRef,
                  },
                },
              },
            },
          }}
        />

        {/* Alert Popup */}
        <AlertPopup
          open={alertPopup.open}
          onClose={handleCancelAction}
          title={
            alertPopup.type === "publish"
              ? "Draft to Publish"
              : alertPopup.type === "unpublish"
              ? "Publish to Draft"
              : alertPopup.type === "error"
              ? "ERROR"
              : "Delete Toy"
          }
          message={
            alertPopup.type === "publish"
              ? "Are you ready to publish this toy?"
              : alertPopup.type === "unpublish"
              ? "Are you sure you want to unpublish this toy?"
              : alertPopup.type === "error"
              ? alertPopup.errorMsg
              : "Are you sure you want to delete this toy?"
          }
          confirmText={
            alertPopup.type === "publish"
              ? "YES, PUBLISH"
              : alertPopup.type === "unpublish"
              ? "YES, UNPUBLISH"
              : alertPopup.type === "error"
              ? "OK"
              : "YES, DELETE"
          }
          onConfirm={handleConfirmAction}
        />
      </ContainerDiv>
    </RootDiv>
  );
}
